/**
 * Microsoft Graph token provider for Outlook via Nested App Authentication (NAA).
 *
 * The Outlook add-in needs mailbox/calendar-WIDE reads (`/me/messages`,
 * `/me/events`) that the Office.js item API cannot provide. Those come from
 * Microsoft Graph, authenticated with NAA + MSAL.js: the Office host brokers the
 * token — no backend, no client secret, no OBO exchange. This module is the
 * "harness" the Graph tool handlers call to get an access token.
 *
 * Design (see Research fragment ce2f29a7 — spike PROVEN on Outlook for Mac):
 *  - Two independent tokens. This Graph token authenticates *Microsoft* and NEVER
 *    crosses the postMessage chat bridge — the handler uses it in-pane, only the
 *    Graph *results* flow back to the model. (Keycloak still authenticates the chat
 *    embed separately.)
 *  - `acquireGraphToken` is silent-first: the agent path calls it non-interactively
 *    and it either returns a cached token or throws `GraphConsentRequired` — it
 *    NEVER pops UI (browsers block agent-triggered popups; the bridge would time
 *    out). Interactive consent (`acquireTokenPopup`) fires only from the "Connect
 *    Microsoft 365" button, where a click is the required user gesture.
 *
 * This is an opt-in host capability (same convention as PowerPoint's `addImage`):
 * only Outlook wires it in via `bootstrap({ graph: … })`; other hosts ignore it.
 */

import {
  createNestablePublicClientApplication,
  InteractionRequiredAuthError,
  type IPublicClientApplication,
} from "@azure/msal-browser";

/**
 * Thrown by `acquireGraphToken` on the silent (agent) path when Microsoft consent
 * is needed — no account is connected yet, the cached token can't be refreshed
 * silently, or the user explicitly disconnected. The shell maps this to a
 * structured `{ status: "interaction_required" }` tool result and the model
 * relays the message verbatim, so the message must describe the REAL UI: the menu
 * (☰, top-left) → "Connect Microsoft 365" at the bottom of the drawer.
 */
export class GraphConsentRequired extends Error {
  constructor(
    message = 'You are not connected to Microsoft 365. To enable this, open the menu (the ☰ button at the top-left of the chat pane) and click "Connect Microsoft 365" at the bottom, then ask again.'
  ) {
    super(message);
    this.name = "GraphConsentRequired";
  }
}

/** The structured tool result a model sees when Microsoft 365 isn't connected. */
export interface InteractionRequiredResult {
  status: "interaction_required";
  message: string;
}

/**
 * Map a caught error to the structured `interaction_required` tool result when it
 * is a `GraphConsentRequired`, else `null` (the caller rethrows). Centralises the
 * consent→result mapping the shell applies to every tool call, and keeps it
 * unit-testable without standing up the embed.
 */
export function graphConsentToolResult(err: unknown): InteractionRequiredResult | null {
  return err instanceof GraphConsentRequired
    ? { status: "interaction_required", message: err.message }
    : null;
}

export interface GraphAuthConfig {
  /** Entra (Azure AD) app-registration client (application) ID. */
  clientId: string;
  /**
   * Tenant-specific authority (`https://login.microsoftonline.com/<tenantId>`).
   * A single-tenant app reg MUST NOT use `/common` — that errors AADSTS50194.
   */
  authority: string;
}

export interface AcquireGraphTokenOptions {
  /**
   * `true` ONLY from the user-gesture consent button → `acquireTokenPopup`.
   * Default `false` (silent): the agent path, which throws `GraphConsentRequired`
   * instead of popping UI.
   */
  interactive?: boolean;
}

/** The token provider handed to Outlook's Graph tool handlers (via `caps`). */
export interface GraphAuth {
  /**
   * Acquire a Graph access token for `scopes`. Silent-first; on the silent path a
   * missing account or an `InteractionRequiredAuthError` throws
   * `GraphConsentRequired`. With `{ interactive: true }` it launches the
   * host-brokered consent popup (consent-button only) and returns the token.
   */
  acquireGraphToken(scopes: string[], options?: AcquireGraphTokenOptions): Promise<string>;
  /** Whether the current Office host supports NAA (requirement set 1.1). */
  isNaaSupported(): boolean;
  /**
   * "Disconnect Microsoft 365". Under NAA the Office host owns the sign-in and
   * MSAL's cache-clearing (`clearCache`, `logout*`) throws `unsupported_method`,
   * so we cannot hard-revoke here. Instead we set a local "disconnected" flag that
   * gates the silent path — tools then return interaction_required until the user
   * reconnects from the button. Resolves without throwing.
   */
  disconnect(): Promise<void>;
}

// A user's explicit "Disconnect" is remembered in localStorage (keyed per client
// id) and gates the silent path. Needed because NAA won't let us clear MSAL's own
// cache — without this, a silent acquire would keep succeeding off the host
// session and "Disconnect" would do nothing. Best-effort: if localStorage is
// unavailable the flag simply doesn't persist (worst case: disconnect is a no-op).
function disconnectedKey(clientId: string): string {
  return `usable.graph.disconnected.${clientId}`;
}
function readDisconnected(clientId: string): boolean {
  try {
    return globalThis.localStorage?.getItem(disconnectedKey(clientId)) === "1";
  } catch {
    return false;
  }
}
function writeDisconnected(clientId: string, value: boolean): void {
  try {
    const key = disconnectedKey(clientId);
    if (value) globalThis.localStorage?.setItem(key, "1");
    else globalThis.localStorage?.removeItem(key);
  } catch {
    /* localStorage unavailable — nothing to persist */
  }
}

/**
 * Build a Graph token provider. The nestable MSAL client is created lazily on the
 * first token request (and memoised), so importing this module costs nothing until
 * a Graph tool actually runs.
 */
export function createGraphAuth(config: GraphAuthConfig): GraphAuth {
  let instancePromise: Promise<IPublicClientApplication> | null = null;

  function getInstance(): Promise<IPublicClientApplication> {
    if (!instancePromise) {
      instancePromise = createNestablePublicClientApplication({
        auth: { clientId: config.clientId, authority: config.authority },
        cache: { cacheLocation: "localStorage" },
      });
    }
    return instancePromise;
  }

  async function acquireGraphToken(
    scopes: string[],
    options: AcquireGraphTokenOptions = {}
  ): Promise<string> {
    // Consent-button path: a click is the user gesture browsers require to allow a
    // popup. A successful (re)connect clears any prior "disconnected" flag.
    if (options.interactive) {
      const instance = await getInstance();
      const result = await instance.acquireTokenPopup({ scopes });
      instance.setActiveAccount(result.account);
      writeDisconnected(config.clientId, false);
      return result.accessToken;
    }

    // Silent (agent) path — never pops UI (see module docs). If the user explicitly
    // disconnected, require the button again even though the host session may still
    // be usable (we can't clear MSAL's cache under NAA, so the flag is the gate).
    if (readDisconnected(config.clientId)) {
      throw new GraphConsentRequired();
    }
    const instance = await getInstance();
    const account = instance.getActiveAccount() ?? instance.getAllAccounts()[0] ?? null;
    if (!account) {
      throw new GraphConsentRequired();
    }
    try {
      const result = await instance.acquireTokenSilent({ scopes, account });
      return result.accessToken;
    } catch (err) {
      if (err instanceof InteractionRequiredAuthError) {
        throw new GraphConsentRequired();
      }
      throw err;
    }
  }

  function isNaaSupported(): boolean {
    return (
      typeof Office !== "undefined" &&
      !!Office.context?.requirements?.isSetSupported?.("NestedAppAuth", "1.1")
    );
  }

  async function disconnect(): Promise<void> {
    // NAA won't let us clear MSAL's cache (clearCache/logout* throw
    // `unsupported_method`), so "disconnect" is a local flag that gates the silent
    // path until the user reconnects from the button. Never throws.
    writeDisconnected(config.clientId, true);
  }

  return { acquireGraphToken, isNaaSupported, disconnect };
}
